import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Line, AreaChart, Area, Cell, ComposedChart, ReferenceLine,
} from 'recharts'
import { BarChart3, TrendingUp, BookOpen, Award, Calendar, Hash, ArrowUpRight } from 'lucide-react'
import { analyticsApi } from '../api/search'
import Plot from 'react-plotly.js'

const QUARTILE_COLORS: Record<string, string> = {
  'Q1': '#28a745',
  'Q2': '#ffc107',
  'Q3': '#fd7e14',
  'Q4': '#dc3545',
  'N/A': '#9ca3af',
}

function StatCard({ title, value, subtitle, icon: Icon, color }: { 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon: any; 
  color: string 
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  )
}

function ChartCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-800 mb-3">{title}</h3>
      {children}
    </div>
  )
}

interface AnalyticsPageProps {
  searchId?: string | null
  iciteFetchTime?: number
}

export default function AnalyticsPage({ searchId: propSearchId, iciteFetchTime }: AnalyticsPageProps = {}) {
  const [searchParams] = useSearchParams()
  const searchId = propSearchId || searchParams.get('search_id')
  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'citations'>('overview')

  const shouldPoll = !!iciteFetchTime && iciteFetchTime > 0 && (Date.now() - iciteFetchTime) < 120000

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics-overview', searchId],
    queryFn: () => searchId ? analyticsApi.getOverview(searchId) : Promise.reject('No search ID'),
    enabled: !!searchId,
    refetchInterval: shouldPoll ? 5000 : false,
  })

  const { data: trends } = useQuery({
    queryKey: ['analytics-trends', searchId],
    queryFn: () => searchId ? analyticsApi.getTrends(searchId) : Promise.reject('No search ID'),
    enabled: !!searchId,
    refetchInterval: shouldPoll ? 5000 : false,
  })

  const { data: keywords } = useQuery({
    queryKey: ['analytics-keywords', searchId],
    queryFn: () => searchId ? analyticsApi.getKeywords(searchId) : Promise.reject('No search ID'),
    enabled: !!searchId,
    refetchInterval: shouldPoll ? 5000 : false,
  })

  const { data: journalMetrics } = useQuery({
    queryKey: ['analytics-journals', searchId],
    queryFn: () => searchId ? analyticsApi.getJournalMetrics(searchId) : Promise.reject('No search ID'),
    enabled: !!searchId,
    refetchInterval: shouldPoll ? 5000 : false,
  })

  const { data: quartileTrendsData } = useQuery({
    queryKey: ['analytics-quartile-trends', searchId],
    queryFn: () => searchId ? analyticsApi.getQuartileTrends(searchId) : Promise.reject('No search ID'),
    enabled: !!searchId,
    refetchInterval: shouldPoll ? 5000 : false,
  })

  // Computed data for trends
  const trendStats = useMemo(() => {
    if (!overview?.year_distribution || overview.year_distribution.length === 0) return null
    
    const years = overview.year_distribution
    const total = overview.total
    const nYears = years.length
    const avgPerYear = Math.round((total / nYears) * 10) / 10
    const maxYear = years.reduce((max, y) => y.count > max.count ? y : max, years[0])
    
    return {
      total,
      nYears,
      avgPerYear,
      maxYear: maxYear.year,
      maxCount: maxYear.count,
      firstYear: years[0].year,
      lastYear: years[years.length - 1].year,
    }
  }, [overview])

  // Growth rate data
  const growthData = useMemo(() => {
    if (!trends?.trends || trends.trends.length < 2) return []
    
    return trends.trends.map((item, index) => {
      if (index === 0) return { ...item, growth_rate: 0, positive: true }
      const prev = trends.trends[index - 1]
      const growth = ((item.count - prev.count) / prev.count) * 100
      return {
        ...item,
        growth_rate: Math.round(growth * 10) / 10,
        positive: growth >= 0,
      }
    })
  }, [trends])

  // Quartile trends over time
  const quartileTrends = useMemo(() => {
    return quartileTrendsData?.quartile_trends || []
  }, [quartileTrendsData])

  if (!searchId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">No Search Selected</h2>
          <p className="text-gray-500 mt-2">Please perform a search first to view analytics.</p>
        </div>
      </div>
    )
  }

  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">
            {overview?.total || 0} articles analyzed
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Articles"
          value={overview?.total || 0}
          icon={BookOpen}
          color="bg-blue-500"
        />
        <StatCard
          title="Years Covered"
          value={trendStats?.nYears || 'N/A'}
          subtitle={trendStats ? `${trendStats.firstYear}-${trendStats.lastYear}` : undefined}
          icon={Calendar}
          color="bg-purple-500"
        />
        <StatCard
          title="Avg per Year"
          value={trendStats?.avgPerYear || 'N/A'}
          icon={TrendingUp}
          color="bg-green-500"
        />
        <StatCard
          title="Peak Year"
          value={trendStats?.maxCount || 'N/A'}
          subtitle={trendStats ? `in ${trendStats.maxYear}` : undefined}
          icon={Award}
          color="bg-amber-500"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {(['overview', 'trends', 'citations'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Publications per Year">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={overview?.year_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3498db" opacity={0.8} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Journal Quartile Distribution">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={overview?.quartile_counts || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="quartile" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count">
                    {(overview?.quartile_counts || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={QUARTILE_COLORS[entry.quartile] || '#9ca3af'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Top 20 MeSH Terms">
              <Plot
                data={[{
                  type: 'bar',
                  orientation: 'h',
                  y: (keywords?.top_mesh?.slice(0, 20) || []).map((d: any) => d.mesh).reverse(),
                  x: (keywords?.top_mesh?.slice(0, 20) || []).map((d: any) => d.count).reverse(),
                  marker: { color: '#e74c3c', opacity: 0.8 },
                  text: (keywords?.top_mesh?.slice(0, 20) || []).map((d: any) => String(d.count)).reverse(),
                  textposition: 'outside',
                  hovertemplate: '<b>%{y}</b><br>Count: %{x}<extra></extra>',
                }]}
                layout={{
                  autosize: true,
                  height: 400,
                  margin: { l: 160, r: 60, t: 10, b: 40 },
                  xaxis: { title: 'Count', gridcolor: '#f0f0f0' },
                  yaxis: { automargin: true, tickfont: { size: 11 } },
                  paper_bgcolor: 'white',
                  plot_bgcolor: 'white',
                  showlegend: false,
                }}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%' }}
              />
            </ChartCard>

            <ChartCard title="Top 20 Journals">
              <Plot
                data={[{
                  type: 'bar',
                  orientation: 'h',
                  y: (overview?.top_journals?.slice(0, 20) || []).map((d: any) => d.journal).reverse(),
                  x: (overview?.top_journals?.slice(0, 20) || []).map((d: any) => d.count).reverse(),
                  marker: { color: '#9b59b6', opacity: 0.8 },
                  text: (overview?.top_journals?.slice(0, 20) || []).map((d: any) => String(d.count)).reverse(),
                  textposition: 'outside',
                  hovertemplate: '<b>%{y}</b><br>Count: %{x}<extra></extra>',
                }]}
                layout={{
                  autosize: true,
                  height: 400,
                  margin: { l: 160, r: 60, t: 10, b: 40 },
                  xaxis: { title: 'Count', gridcolor: '#f0f0f0' },
                  yaxis: { automargin: true, tickfont: { size: 11 } },
                  paper_bgcolor: 'white',
                  plot_bgcolor: 'white',
                  showlegend: false,
                }}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%' }}
              />
            </ChartCard>
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Publications per Year with Trend">
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={overview?.year_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3498db" opacity={0.6} name="Publications" />
                  <Line type="monotone" dataKey="count" stroke="#e74c3c" strokeWidth={2} dot={{ r: 4 }} name="Trend" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Cumulative Publications">
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={trends?.cumulative || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#3498db" fill="#3498db" fillOpacity={0.3} name="Cumulative" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Year-over-Year Growth Rate (%)">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => `${value}%`} />
                  <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
                  <Bar dataKey="growth_rate">
                    {growthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.positive ? '#27ae60' : '#e74c3c'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Journal Quartile Distribution Over Time">
              {quartileTrends.length > 1 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={quartileTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(value: any) => `${value}%`} />
                    <Legend />
                    {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                      <Area
                        key={q}
                        type="monotone"
                        dataKey={q}
                        stackId="1"
                        stroke={QUARTILE_COLORS[q]}
                        fill={QUARTILE_COLORS[q]}
                        fillOpacity={0.8}
                        name={q}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : quartileTrends.length === 1 ? (
                <div className="flex items-center justify-center h-[350px] text-gray-500">
                  <div className="text-center">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Only 1 year of data ({quartileTrends[0]?.year})</p>
                    <p className="text-xs text-gray-400 mt-1">Search across multiple years to see trends</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[350px] text-gray-500">
                  <p className="text-sm">No quartile trend data available</p>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Keyword Trends */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Keyword Trends Over Time</h3>
            {keywords?.keyword_trends && keywords.keyword_trends.length > 0 ? (
              <div className="space-y-4">
                {keywords.keyword_trends.map((yearData) => (
                  <div key={yearData.year} className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900 text-sm">{yearData.year}</h4>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {yearData.keywords.slice(0, 8).map((kw) => (
                        <span
                          key={kw.keyword}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                        >
                          {kw.keyword} ({kw.count})
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No keyword trend data available.</p>
            )}
          </div>
        </div>
      )}

      {/* Citations Tab */}
      {activeTab === 'citations' && (
        <div className="space-y-6">
          {overview?.citation_stats && Object.keys(overview.citation_stats).length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  title="Mean Citations"
                  value={overview.citation_stats.mean}
                  icon={ArrowUpRight}
                  color="bg-blue-500"
                />
                <StatCard
                  title="Median Citations"
                  value={overview.citation_stats.median}
                  icon={Hash}
                  color="bg-green-500"
                />
                <StatCard
                  title="Max Citations"
                  value={overview.citation_stats.max}
                  subtitle={`${overview.citation_stats.total_with_data} articles with data`}
                  icon={Award}
                  color="bg-purple-500"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Citation Distribution">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={journalMetrics?.sjr_distribution || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3498db" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="SJR vs Citations">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={journalMetrics?.quartile_avg_sjr || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="quartile" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="avg_sjr" fill="#9b59b6" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No Citation Data Available</h3>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Click "Fetch iCite Metrics" in the left sidebar to load citation data from NIH iCite API.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
